"use client";

import { Plus, Trash2 } from "lucide-react";
import { cn } from "~/lib/utils";
import { CopyField } from "./copy-field";
import { SecretNote } from "./secret-note";
import { mcpConfig } from "../../lib/mcp-config";
import { CodexDesktopIcon } from "../ClientIcon";

// // // //

/**
 * CodexInstructions
 * Reproduces Codex Desktop's "Connect to a custom MCP" (Streamable HTTP) dialog
 * as styled markup (see .notes/assets/codex-desktop-setup.png). The only
 * interactive fields are Name, URL, and the Bearer <key> header value; the rest
 * (env-var field, extra-header rows, add/save buttons) is static chrome so the
 * mockup is recognizable. Reused in placeholder mode (copy off) and live mode.
 */
export function CodexInstructions(props: {
    apiKey: string;
    enableCopy?: boolean;
}) {
    const { apiKey, enableCopy = true } = props;

    return (
        <div className="flex flex-col gap-4">
            <ol className="flex flex-col gap-1.5 text-sm leading-relaxed text-muted-foreground">
                <Numbered n={1}>
                    In Codex, open the{" "}
                    <span className="text-foreground">Settings</span> page.
                </Numbered>
                <Numbered n={2}>
                    Under <span className="text-foreground">Integrations</span>{" "}
                    in the left sidebar, click{" "}
                    <span className="text-foreground">MCP servers</span>.
                </Numbered>
                <Numbered n={3}>
                    Click <span className="text-foreground">Add Server</span>,
                    then select{" "}
                    <span className="text-foreground">Streamable HTTP</span>.
                </Numbered>
                <Numbered n={4}>
                    Fill in the fields below, then click{" "}
                    <span className="text-foreground">Save</span>.
                </Numbered>
            </ol>

            <div>
                <div className="mb-2 flex items-center gap-2">
                    <CodexDesktopIcon className="size-4" />
                    <span className="text-xs font-medium text-muted-foreground">
                        Codex Desktop
                    </span>
                </div>

                <div className="dark flex flex-col gap-3 rounded-lg border bg-card p-4 text-foreground">
                    <div className="mb-1 flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">
                            Connect to a custom MCP
                        </span>
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary-foreground">
                            Streamable HTTP
                        </span>
                    </div>

                    <CodexField label="Name">
                        <CopyField
                            label=""
                            value={mcpConfig.serverName}
                            mono={false}
                            enableCopy={enableCopy}
                        />
                    </CodexField>

                    <CodexField label="URL">
                        <CopyField
                            label=""
                            value={mcpConfig.serverUrl}
                            enableCopy={enableCopy}
                        />
                    </CodexField>

                    <CodexField label="Bearer token env var">
                        <FauxInput muted>
                            MCP_BEARER_TOKEN (Ignore this)
                        </FauxInput>
                    </CodexField>

                    <CodexField label="Headers">
                        <div className="flex items-center gap-2">
                            <FauxInput className="w-[40%] shrink-0">
                                Authorization
                            </FauxInput>
                            <CopyField
                                label=""
                                value={`Bearer ${apiKey}`}
                                secret={enableCopy}
                                enableCopy={enableCopy}
                                className="min-w-0 flex-1"
                            />
                            <Trash2
                                className="size-4 shrink-0 text-muted-foreground"
                                aria-hidden="true"
                            />
                        </div>
                        <FauxButton label="Add header" />
                    </CodexField>

                    <CodexField label="Headers from environment variables">
                        <div className="flex items-center gap-2">
                            <FauxInput muted className="flex-1">
                                Key
                            </FauxInput>
                            <FauxInput muted className="flex-1">
                                Value
                            </FauxInput>
                            <Trash2
                                className="size-4 shrink-0 text-muted-foreground/50"
                                aria-hidden="true"
                            />
                        </div>
                        <FauxButton label="Add variable" />
                    </CodexField>

                    <div className="flex justify-end pt-1">
                        <span className="rounded-md bg-foreground px-4 py-1.5 text-xs font-medium text-background">
                            Save
                        </span>
                    </div>
                </div>
            </div>

            {/* Only warn about the real key in the live wizard, not the
                placeholder preview. */}
            {enableCopy ? <SecretNote /> : null}
        </div>
    );
}

function CodexField({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground">{label}</span>
            {children}
        </div>
    );
}

function FauxInput({
    children,
    muted = false,
    className,
}: {
    children: React.ReactNode;
    muted?: boolean;
    className?: string;
}) {
    return (
        <div
            className={cn(
                "flex items-center rounded-md border bg-muted px-3 py-2 text-sm",
                muted ? "text-muted-foreground" : "text-foreground",
                className,
            )}
        >
            {children}
        </div>
    );
}

function FauxButton({ label }: { label: string }) {
    return (
        <div className="flex items-center justify-center gap-1.5 rounded-md border bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
            <Plus className="size-3.5" aria-hidden="true" />
            {label}
        </div>
    );
}

function Numbered({ n, children }: { n: number; children: React.ReactNode }) {
    return (
        <li className="flex gap-2.5">
            <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-secondary-foreground">
                {n}
            </span>
            <span>{children}</span>
        </li>
    );
}
